/**
 * Style Guide Page
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Comprehensive design system documentation page showcasing all reusable styles,
 * components, colors, typography, and patterns used throughout the website.
 * Serves as a living style guide for developers and designers.
 * 
 * Features:
 * - Color palette with hex codes
 * - Typography hierarchy and sizes
 * - Button styles and states
 * - Form elements
 * - Card components
 * - Spacing and layout patterns
 * - Icon usage
 * - Responsive breakpoints
 * - Animation patterns
 * 
 * Dependencies:
 * - React 19.2.4 (JSX)
 * - TailwindCSS 4.1 (styling)
 * - ProIcon 1.0 (icons)
 * - Framer Motion 12 (animations)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with comprehensive design system documentation
 */

import React, { useState } from 'react';
import { ProIcon } from '../components/ProIcon';
import { DuotoneIcon, iconSchemes } from '../components/DuotoneIcon';
import { DESIGN_TOKENS } from '../designTokens';

export const StyleGuide = () => {
  const [copiedColor, setCopiedColor] = useState<string | null>(null);

  const copyToClipboard = (text: string, colorName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedColor(colorName);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  const colors = [
    { name: 'Brand Sun', hex: DESIGN_TOKENS.colors.brandSun, usage: 'Primary CTAs only - use sparingly for conversion focus' },
    { name: 'Brand Ocean', hex: DESIGN_TOKENS.colors.brandOcean, usage: 'Headings and primary text' },
    { name: 'Brand Gray', hex: DESIGN_TOKENS.colors.brandGray, usage: 'Secondary text and subtle elements' },
    { name: 'Brand Sand', hex: DESIGN_TOKENS.colors.brandSand, usage: 'Subtle backgrounds - let images breathe' },
  ];

  const typography = [
    { size: '5xl', weight: 'font-bold', usage: 'Hero titles only', example: 'Your Family\'s Holiday' },
    { size: '3xl', weight: 'font-bold', usage: 'Page headings', example: 'Luxury Rentals in Marbella' },
    { size: 'xl', weight: 'font-semibold', usage: 'Property titles', example: 'Villa Luna del Mar' },
    { size: 'base', weight: 'font-normal', usage: 'Body text - primary content', example: 'Based in Marbella since 2013, we specialize in family-friendly rentals...' },
    { size: 'sm', weight: 'font-normal', usage: 'Small text and captions', example: '€450 per night' },
  ];

  const buttons = [
    { label: 'Primary CTA', className: 'ui-btn-primary', usage: 'Main conversion actions only (Book Now, Search)' },
    { label: 'Secondary', className: 'ui-btn-secondary', usage: 'All other actions (View, Contact, Learn More)' },
  ];

  const spacing = [
    { name: 'Section Y', class: 'py-16 sm:py-24', usage: 'Vertical section padding' },
    { name: 'Gap Large', class: 'gap-6 sm:gap-10', usage: 'Card grid gaps' },
    { name: 'Gap Medium', class: 'gap-4 sm:gap-6', usage: 'Component spacing' },
    { name: 'Gap Small', class: 'gap-2 sm:gap-4', usage: 'Tight spacing' },
  ];

  const borderRadius = [
    { name: 'Rounded 2XL', class: 'rounded-2xl', usage: 'Standard for all cards, buttons, and containers' },
    { name: 'Rounded Full', class: 'rounded-full', usage: 'Avatars and circular elements only' },
  ];

  return (
    <div className="min-h-screen bg-white pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
        {/* Header */}
        <section className="mb-16 sm:mb-24">
          <h1 className="ui-page-title mb-4">Design System</h1>
          <p className="text-lg text-brand-gray max-w-3xl">
            Comprehensive style guide for SUNMARBELLA. Use these components, colors, and patterns to maintain consistency across the website.
          </p>
        </section>

        {/* Color Palette */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Color Palette</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {colors.map((color) => (
              <div key={color.hex} className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className="h-24 w-full" style={{ backgroundColor: color.hex }} />
                <div className="p-4">
                  <h3 className="font-black text-slate-900 mb-1">{color.name}</h3>
                  <button
                    onClick={() => copyToClipboard(color.hex, color.name)}
                    className="flex items-center gap-2 text-sm font-bold text-brand-ocean hover:text-brand-sun transition-colors mb-2"
                  >
                    <code className="bg-slate-100 px-2 py-1 rounded">{color.hex}</code>
                    <ProIcon icon={copiedColor === color.name ? 'check' : 'copy'} family="light" className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-brand-gray">{color.usage}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Typography</h2>
          <div className="space-y-6">
            {typography.map((type) => (
              <div key={type.size} className="border border-slate-200 rounded-2xl p-6">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="font-black text-slate-900">
                    <code className="bg-slate-100 px-2 py-1 rounded text-sm">text-{type.size} {type.weight}</code>
                  </h3>
                  <span className="text-xs text-brand-gray uppercase tracking-widest">{type.usage}</span>
                </div>
                <div className={`text-${type.size} ${type.weight} text-brand-ocean`}>{type.example}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Buttons */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Buttons</h2>
          <div className="space-y-6">
            {buttons.map((btn) => (
              <div key={btn.label} className="border border-slate-200 rounded-2xl p-6">
                <h3 className="font-black text-slate-900 mb-4">{btn.label}</h3>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <button className={btn.className}>
                    {btn.label}
                  </button>
                  <div className="flex-1">
                    <code className="text-xs bg-slate-100 px-3 py-2 rounded block mb-2">{btn.className}</code>
                    <p className="text-sm text-brand-gray">{btn.usage}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Spacing & Layout */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Spacing & Layout</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {spacing.map((space) => (
              <div key={space.class} className="border border-slate-200 rounded-2xl p-6">
                <h3 className="font-black text-slate-900 mb-2">{space.name}</h3>
                <code className="text-xs bg-slate-100 px-3 py-2 rounded block mb-3">{space.class}</code>
                <p className="text-sm text-brand-gray">{space.usage}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Border Radius */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Border Radius</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {borderRadius.map((radius) => (
              <div key={radius.class} className="border border-slate-200 rounded-2xl p-6">
                <h3 className="font-black text-slate-900 mb-4">{radius.name}</h3>
                <div className={`h-20 w-20 bg-brand-sun mb-4 ${radius.class}`} />
                <code className="text-xs bg-slate-100 px-3 py-2 rounded block mb-3">{radius.class}</code>
                <p className="text-sm text-brand-gray">{radius.usage}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Icons */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Icons</h2>
          <div className="border border-slate-200 rounded-2xl p-8">
            <p className="text-brand-gray mb-6">Using DuotoneIcon component with Font Awesome icons</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-6">
              {['home', 'search', 'calendar-alt', 'map-marker-alt', 'comments', 'star', 'heart', 'check', 'lock', 'phone', 'mail', 'share'].map((icon) => (
                <div key={icon} className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <DuotoneIcon name={icon} size="lg" {...iconSchemes.ocean} />
                  </div>
                  <code className="text-xs text-slate-600">{icon}</code>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Responsive Breakpoints */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Responsive Breakpoints</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-black text-slate-900 mb-2">Mobile</h3>
              <code className="text-xs bg-slate-100 px-3 py-2 rounded block mb-3">Default (0px)</code>
              <p className="text-sm text-brand-gray">Single column, compact spacing, touch-friendly buttons</p>
            </div>
            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-black text-slate-900 mb-2">Tablet (sm)</h3>
              <code className="text-xs bg-slate-100 px-3 py-2 rounded block mb-3">640px and up</code>
              <p className="text-sm text-brand-gray">2 column layout, medium spacing, larger text</p>
            </div>
            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-black text-slate-900 mb-2">Desktop (md)</h3>
              <code className="text-xs bg-slate-100 px-3 py-2 rounded block mb-3">768px and up</code>
              <p className="text-sm text-brand-gray">2-3 column layout, full navigation, expanded features</p>
            </div>
            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-black text-slate-900 mb-2">Large Desktop (lg)</h3>
              <code className="text-xs bg-slate-100 px-3 py-2 rounded block mb-3">1024px and up</code>
              <p className="text-sm text-brand-gray">3-4 column layout, full width content, all features visible</p>
            </div>
          </div>
        </section>

        {/* Component Patterns */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Component Patterns</h2>
          <div className="space-y-6">
            {/* Card Pattern */}
            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Property Card</h3>
              <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden hover:border-slate-200 transition-colors">
                <div className="aspect-[4/3] bg-slate-100"></div>
                <div className="p-4">
                  <h4 className="text-xl font-semibold text-brand-ocean mb-1">Villa Luna del Mar</h4>
                  <p className="text-sm text-brand-gray mb-3">Beachfront rental with heated pool</p>
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold text-slate-900">€450/night</span>
                    <button className="ui-btn-secondary px-4 py-2 text-sm">
                      View
                    </button>
                  </div>
                </div>
              </div>
              <code className="text-xs bg-slate-100 px-3 py-2 rounded block mt-4">
                Minimal borders, subtle hover, image-first design
              </code>
            </div>

            {/* CTA Section Pattern */}
            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">CTA Section</h3>
              <div className="bg-brand-sand rounded-2xl p-8 text-center border border-slate-100">
                <h4 className="text-2xl font-bold text-brand-ocean mb-3">Ready to Book?</h4>
                <p className="text-brand-gray mb-6">Browse our curated collection of family rentals</p>
                <button className="ui-btn-primary">
                  Browse Rentals
                </button>
              </div>
              <code className="text-xs bg-slate-100 px-3 py-2 rounded block mt-4">
                Subtle background, single CTA, no gradients
              </code>
            </div>

            {/* Filter Button Pattern */}
            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Filter Buttons</h3>
              <div className="flex flex-wrap gap-3">
                <button className="ui-btn-primary px-5 py-2.5 text-sm">
                  €200-500
                </button>
                <button className="ui-btn-secondary px-5 py-2.5 text-sm">
                  Property Type
                </button>
                <button className="ui-btn-secondary px-5 py-2.5 text-sm">
                  Amenities
                </button>
              </div>
              <code className="text-xs bg-slate-100 px-3 py-2 rounded block mt-4">
                Active: `ui-btn-primary` | Inactive: `ui-btn-secondary`
              </code>
            </div>
          </div>
        </section>

        {/* Usage Guidelines */}
        <section className="mb-16 sm:mb-24">
          <h2 className="ui-section-title mb-8">Usage Guidelines</h2>
          <div className="space-y-6">
            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-black text-slate-900 mb-3">Company Name</h3>
              <p className="text-brand-gray mb-3">Use the brand as one word only:</p>
              <ul className="text-sm text-brand-gray space-y-2">
                <li>✓ Correct: "SUNMARBELLA Holiday Rentals"</li>
                <li>✓ Also valid: "Sunmarbella" when title case is needed</li>
                <li>✓ Technical: "sunmarbella" for URLs, emails, and test environments</li>
                <li>✗ Incorrect: "Sun Marbella" or any two-word version</li>
              </ul>
            </div>

            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-3">Color Usage - Minimalist Approach</h3>
              <ul className="text-sm text-brand-gray space-y-2">
                <li>✓ Use <span className="font-semibold text-brand-sun">brand-sun ({DESIGN_TOKENS.colors.brandSun})</span> ONLY for primary CTAs - sparingly for conversion focus</li>
                <li>✓ Use <span className="font-semibold text-brand-ocean">brand-ocean ({DESIGN_TOKENS.colors.brandOcean})</span> for all headings and primary text</li>
                <li>✓ Use <span className="font-semibold text-brand-gray">brand-gray ({DESIGN_TOKENS.colors.brandGray})</span> for secondary text</li>
                <li>✗ NO gradients, NO multiple orange/blue shades - keep it simple</li>
                <li>✓ Let property images be the visual focus, not colors</li>
              </ul>
            </div>

            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-3">Typography - Simplified Scale</h3>
              <ul className="text-sm text-brand-gray space-y-2">
                <li>✓ Use <span className="font-semibold">text-5xl font-bold</span> for hero titles only</li>
                <li>✓ Use <span className="font-semibold">text-3xl font-bold</span> for page headings</li>
                <li>✓ Use <span className="font-semibold">text-xl font-semibold</span> for property titles</li>
                <li>✓ Use <span className="font-semibold">text-base font-normal</span> for all body text</li>
                <li>✓ Reserve font-black for short headings and labels; use font-bold/font-semibold for body copy</li>
              </ul>
            </div>

            <div className="border border-slate-200 rounded-2xl p-6">
              <h3 className="font-black text-slate-900 mb-3">Spacing</h3>
              <ul className="text-sm text-brand-gray space-y-2">
                <li>✓ Use <span className="font-bold">py-16 sm:py-24</span> for section vertical padding</li>
                <li>✓ Use <span className="font-bold">gap-6 sm:gap-10</span> for card grid gaps</li>
                <li>✓ Use <span className="font-bold">max-w-7xl</span> for container width</li>
                <li>✓ Always include responsive breakpoints (sm:, md:, lg:)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-brand-sand rounded-2xl p-8 sm:p-12 text-center border border-slate-100">
          <h2 className="ui-title-compact mb-3">Design Principles</h2>
          <p className="text-base text-brand-gray max-w-2xl mx-auto">
            Keep it minimal. Let images shine. Use orange sparingly. Focus on conversion.
          </p>
        </section>
      </div>
    </div>
  );
};
